import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  TextField,
  FormControl,
  Select,
  Stack,
  MenuItem,
  Typography,
  Breadcrumbs,
  InputLabel,
  IconButton,
  Grid,
  Alert,
  Box,
  Chip,
  Paper,
  Divider,
} from "@mui/material";
import { HomeRounded } from "@mui/icons-material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import ApiCall from "../../utils/apicall";

// ASTM A6/A6M §4 product-form names keyed off the inventory shape enum.
const SHAPE_FORM = {
  W: "Wide-flange shape (W)", M: "Misc. I-shape (M)", S: "Standard I-shape (S)",
  HP: "Bearing pile (HP)", C: "Channel (C)", MC: "Misc. channel (MC)",
  L: "Angle (L)", "2L": "Double angle (2L)", WT: "Tee from W (WT)",
  MT: "Tee from M (MT)", ST: "Tee from S (ST)", HSS: "Hollow structural section (HSS)",
  PIPE: "Steel pipe (PIPE)", PL: "Plate (PL)", PLT: "Plate (PLT)",
};

const fmtLen = (ft, inch) => {
  const f = Number(ft) || 0;
  const i = Number(inch) || 0;
  if (!f && !i) return "—";
  return `${f}' ${i}"`;
};

// Read-only ASTM A6/A6M §4 ordering-detail panel for a selected inventory line.
const A6LineDetail = ({ inv }) => {
  if (!inv) return null;
  const rows = [
    ["Product form (A6 §4 b)", SHAPE_FORM[inv.shape] || inv.shape || "—"],
    ["Designation / size (A6 §4 d)", inv.aiscManualLabel || inv.ediStdNomenclature || "—"],
    ["ASTM spec / grade (A6 §4 c)", inv.grade || "—"],
    ["Length on hand (A6 §4 e)", fmtLen(inv.lengthReceivedFoot, inv.lengthReceivedInch)],
    ["Nominal weight", inv.weight != null ? `${inv.weight} lb/ft` : "—"],
    ["Depth", inv.depth != null ? `${inv.depth} in` : "—"],
    ["Heat # (traceability)", inv.heatNumber || "—"],
    ["Stock on hand", inv.quantity != null ? `${inv.quantity}` : "—"],
  ];
  return (
    <Paper
      variant="outlined"
      sx={{ mt: 1.5, mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 2 }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          ASTM A6/A6M §4 ordering detail
        </Typography>
        <Chip
          size="small"
          label={inv.orderArrivedCMTR ? "CMTR required" : "CMTR — verify"}
          color={inv.orderArrivedCMTR ? "success" : "warning"}
          variant="outlined"
        />
        <Chip
          size="small"
          label={inv.itemType === "job" ? "Job material" : "Stock material"}
          variant="outlined"
        />
      </Stack>
      <Divider sx={{ mb: 1 }} />
      <Grid container spacing={1}>
        {rows.map(([k, v]) => (
          <Grid item xs={12} sm={6} key={k}>
            <Typography variant="caption" color="text.secondary">
              {k}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {v}
            </Typography>
          </Grid>
        ))}
      </Grid>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        Spec, grade, dimensions and heat traceability carry from the linked
        inventory record (single source of truth). Confirm the ASTM edition
        year and unit system (A6 vs A6M) on the PO header.
      </Typography>
    </Paper>
  );
};

const CreatePurchaseOrderItem = () => {
  const [inventory_id, setInventoryId] = useState("");
  const [quantity, setQuantity] = useState();
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const [inventoryData, setInventoryData] = useState([]);
  const [items, setItems] = useState([{ inventory_id: "", quantity: 1 }]);
  const { id } = useParams();
  const fetchOrder = async () => {
    try {
      const response = await ApiCall.get("/inventory");
      const data = response.data.data.inventories;
      setInventoryData(data || []);
    } catch (error) {
      console.error("Error fetching inventory data", error);
    }
  };

  const findInv = (invId) =>
    inventoryData.find((d) => String(d.id) === String(invId));

  const validateDuplicates = (items) => {
    const uniqueInventoryIds = new Set();
    const duplicateInventoryIds = [];

    for (const item of items) {
      if (uniqueInventoryIds.has(item.inventory_id)) {
        duplicateInventoryIds.push(item.inventory_id);
      }
      uniqueInventoryIds.add(item.inventory_id);
    }

    return duplicateInventoryIds;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const item of items) {
      if (item.quantity === "" || isNaN(item.quantity) || item.quantity <= 0) {
        toast.error("Quantity is required and must be a non-negative number", {
          position: "top-right",
        });
        return;
      }
    }
    if (items.some((item) => !item.inventory_id)) {
      toast.error("Inventory is required for all items", {
        position: "top-right",
      });
      return;
    }
    if (inventoryData.length === 0) {
      toast.error("No inventory data available", { position: "top-right" });
      return;
    }
    const duplicateInventoryIds = validateDuplicates(items);

    if (duplicateInventoryIds.length > 0) {
      toast.error("Duplicate inventory items", { position: "top-right" });
      return;
    } else
      try {
        for (const item of items) {
          const { inventory_id, quantity } = item;
          const response = await ApiCall.post("/purchaseorderitem", {
            po_id: id,
            inventory_id,
            quantity,
          });
          if (response.status === 201) {
            console.log("Data saved successfully");
            navigate("/purchaseorder");
          } else {
            console.error("Error saving data");
          }

          // Clear the items array and reset form fields
          setItems([{ inventory_id: "", quantity: 1 }]);
          setInventoryId("");
          setQuantity(1);
          setErrors({});
        }
      } catch (error) {
        // console.error("An error occurred:", error);
      }
  };

  const handleAddItem = () => {
    setItems([...items, { inventory_id: "", quantity: 1 }]);
    setInventoryId("");
  };

  const handleRemoveItem = (index) => {
    if (items.length === 1) {
      return;
    }

    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    setItems(updatedItems);
  };

  useEffect(() => {
    fetchOrder();
  }, []);

  return (
    <div>
      <Helmet>
        <title>Purchase Order Items | SFM</title>
      </Helmet>
      <container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Breadcrumbs aria-label="breadcrumb">
            <Stack direction="row" alignItems="center" spacing={1}>
              <HomeRounded color="inherit" />
              <Typography variant="body1" color="textPrimary">
                / PurchaseOrder Items
              </Typography>
            </Stack>
          </Breadcrumbs>
        </Stack>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>ASTM A6/A6M §4 ordering information.</strong> Each line
            below references an inventory item that carries its product form,
            ASTM spec/grade, dimensions, length and heat-traceability. Pick the
            item and quantity ordered — the A6 §4 detail and CMTR-required flag
            display for confirmation. Set the ASTM edition year and unit system
            (A6 vs A6M) on the PO header.
          </Typography>
        </Alert>

        <form onSubmit={handleSubmit}>
          {items.map((item, index) => {
            const inv = findInv(item.inventory_id);
            return (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={2}>
                    <Typography variant="overline" color="text.secondary">
                      Line {index + 1}
                    </Typography>
                    <Box>
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        color="secondary"
                        size="small"
                      >
                        <RemoveIcon />
                      </IconButton>
                      <IconButton
                        onClick={handleAddItem}
                        color="primary"
                        size="small"
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel htmlFor={`inventory-id-${index}`}>
                        Select Inventory
                      </InputLabel>
                      <Select
                        label="Select Inventory"
                        value={item.inventory_id}
                        onChange={(e) => {
                          const updatedItems = [...items];
                          updatedItems[index].inventory_id = e.target.value;
                          setItems(updatedItems);
                        }}
                        inputProps={{
                          name: `inventory-${index}`,
                          id: `inventory-id-${index}`,
                        }}
                      >
                        <MenuItem value="">
                          <em>Select an Inventory</em>
                        </MenuItem>
                        {inventoryData.map((dataItem) => (
                          <MenuItem key={dataItem.id} value={dataItem.id}>
                            {dataItem.aiscManualLabel || dataItem.ediStdNomenclature}
                            {dataItem.grade ? ` · ${dataItem.grade}` : ""}
                            {dataItem.shape ? ` · ${dataItem.shape}` : ""}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors[index] && (
                      <div style={{ color: "red", fontSize: "12px" }}>
                        {errors[index].inventory_id}
                      </div>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Quantity (A6 §4 a)"
                      type="number"
                      fullWidth
                      value={item.quantity}
                      onChange={(e) => {
                        const updatedItems = [...items];
                        updatedItems[index].quantity = e.target.value;
                        setItems(updatedItems);
                      }}
                    />
                    {errors[index] && (
                      <div style={{ color: "red", fontSize: "12px" }}>
                        {errors[index].quantity}
                      </div>
                    )}
                  </Grid>
                </Grid>
                <A6LineDetail inv={inv} />
              </Box>
            );
          })}
          <Button type="submit" variant="contained" color="primary">
            Submit
          </Button>
        </form>
      </container>
    </div>
  );
};

export default CreatePurchaseOrderItem;
